
import { defineComponent, reactive, ref } from "vue";
import { useUserStore } from "@/store";
import useSelectDialog from "@/hooks/useSelectDialog";
import ShoninComment from "@/views/shoninComment/index.vue";
import {
  getIsSameGroupFlg,
  getShinseiBunruiList,
  getShinseiById,
} from "@/api/shinsei.api";
import { shoninCommentHojoInfo } from "../shoninComment/config";
import { useRoute, useRouter } from "vue-router";
import { injectScrollToTop } from "@/hooks/useScrollbar";
import { shinseiModel, shoninModel } from "@/interfaces/shinsei.interface";
import { ELEMENT_ACCESS } from "@/constants/accessList.constant";
import { dateFormat } from "@/utils/date";
import { Status } from "@/constants/common.constant";
import { generateButtons, statusFilter } from "./common";
import { handleShoninshaButtons } from "./handleShoninshaButtons";
import { handleShinseishaButtons } from "./handleShinseishaBunttos";
import { CheckMsg } from "@/constants/message.constant";
import dayjs from "dayjs";
import { injectReloadFunc } from "@/hooks/useReload";
import { Buttons } from "./config";
import Doijiko from "@/views/terms/doi.vue";

export default defineComponent({
  components: { ShoninComment, Doijiko },
  setup() {
    injectScrollToTop();
    const panels = reactive({
      shinseiInfo: {
        id: 0,
        shinseiId: "",
        statusCd: "",
        status: "",
        shinchokuCdShinsei: "",
        shinchokuCdShonin: "",
        shinchokuShinsei: "",
        shinchokuShonin: "",
        torokuDate: "",
        kousinDate: "",
      },
      shinseishaInfo: {
        kaishaNm: "",
        groupId: "",
        groupNm: "",
        torokuKjnNm: "",
        torokuBushoNm: "",
        kanriKjnNm: "",
        kanriBushoNm: "",
      },
      shoninshaInfo: {
        torokuTantoNm: "",
        kanriTantoNm: "",
      },
      shinseiForm: {
        itakusakiKaishaNm: "",
        itakusakiJusho: "",
        saiItakusakiKaishaNm: "",
        saiItakusakiJusho: "",
        kikanKaishibi: null,
        kikanShuryobi: null,
        riyoBasho: "",
        riyomokuteki: "",
        riyohoho: "",
        riyohaniDcfFlg: false,
        riyohaniDsfFlg: false,
        riyohaniDnfFlg: false,
        riyohaniEcfFlg: false,
        riyohaniPcfFlg: false,
        riyohaniSonohokaFlg: false,
        riyohaniSonohokaText: "",
        riyokomokuKjnCdFlg: false,
        riyokomokuKjnNmFlg: false,
        riyokomokuShiCdFlg: false,
        riyokomokuShiNmFlg: false,
        riyokomokuShiJushoFlg: false,
        riyokomokuShiTelFlg: false,
        riyokomokuSrkmFlg: false,
        riyokomokuSzbkFlg: false,
        riyokomokuSonohokaFlg: false,
        riyokomokuSonohokaText: "",
        shinseiComment: "",
        doiCheck: false,
        torokuAccountId: "",
        kanriAccountId: "",
      } as shinseiModel,

      shoninForm: {
        shinseiBunruiCd: "",
        shoninComment: "",
        shoninTorokuAccountId: "",
        shoninKanriAccountId: "",
      } as shoninModel,
    });
    const shinseiFormRef = ref();
    const shoninFormRef = ref();
    const loadings = reactive({
      //申請者用button
      shinseiBtn: false,
      kariShinseiBtn: false,
      kariShinseiOkBtn: false,
      copyBtn: false,
      saveBtn: false,
      deleteBtn: false,
      kariShinseiNgBtn: false,

      //承認者用button
      shoninBtn: false,
      kariShoninBtn: false,
      kariShoninOkBtn: false,
      horyuBtn: false,
      kariHoryuBtn: false,
      kariHoryuOkBtn: false,
      kariShoninNgBtn: false,
      kariHoryuNgBtn: false,
    } as Buttons);
    const { displayButtons, displayPatterns } = generateButtons(); //ボタン表示制御用
    const shinseiBunruiOptions = ref([]); //申請分類選択リスト
    const isShinseiPanelDisable = ref(false); //申請内容記入欄活性制御変数
    const isShoninPanelDisable = ref(true); //承認内容記入欄活性制御変数
    const isShoninPanelVisible = ref(false); //承認内容記入欄表示制御変数
    const userStore = useUserStore();
    const router = useRouter();
    const route = useRoute();
    const reload = injectReloadFunc();
    const handleReload = (id?: string) => {
      if (panels.shinseiInfo.id) {
        reload();
      } else if (id) {
        router.push({ name: "shinseiDetail", params: { id } });
      }
    };

    // 入力情報のチェックルール;
    const shinseiFormRules = {
      itakusakiKaishaNm: [
        {
          required: true,
          trigger: "blur",
          message: CheckMsg.CHK_SHINSEI_ITAKUSAKI_KAISHA_NM,
        },
        {
          validator: async (self: any, value: string, callback: (message: string) => void) => {
            if (panels.shinseiForm.itakusakiKaishaNm.trim() === '') {
              callback(CheckMsg.CHK_SHINSEI_ITAKUSAKI_KAISHA_NM)
            }
          }
        }
      ],
      itakusakiJusho: [
        {
          required: true,
          trigger: "blur",
          message: CheckMsg.CHK_SHINSEI_ITAKUSAKI_JUSHO,
        },
        {
          validator: async (self: any, value: string, callback: (message: string) => void) => {
            if (panels.shinseiForm.itakusakiJusho.trim() === '') {
              callback(CheckMsg.CHK_SHINSEI_ITAKUSAKI_JUSHO)
            }
          }
        }
      ],
      kikanKaishibi: [
        {
          required: true,
          trigger: "change",
          message: CheckMsg.CHK_SHINSEI_KAISHIBI,
        },
      ],
      kikanShuryobi: [
        {
          required: true,
          trigger: "change",
          message: CheckMsg.CHK_SHINSEI_SHURYOBI,
        },
        {
          validator: async (self: any, value: string, callback: any) => {
            if (
              panels.shinseiForm.kikanKaishibi &&
              panels.shinseiForm.kikanShuryobi
            ) {
              //開始日>=終了日の場合、エラー
              if (
                panels.shinseiForm.kikanKaishibi >=
                panels.shinseiForm.kikanShuryobi
              ) {
                callback(CheckMsg.CHK_SHINSEI_VALID_DATE);
              }
              //終了日は開始日より1年以上先の場合、エラー
              else if (
                dayjs(panels.shinseiForm.kikanShuryobi).diff(
                  panels.shinseiForm.kikanKaishibi,
                  "year"
                ) >= 1
              ) {
                callback(CheckMsg.CHK_SHINSEI_ONE_YEAR_DATE);
              }
            }
          },
          trigger: "change",
        },
      ],
      riyoBasho: [
        {
          required: true,
          trigger: "blur",
          message: CheckMsg.CHK_SHINSEI_RIYO_BASHO,
        },
        {
          validator: async (self: any, value: string, callback: (message: string) => void) => {
            if (panels.shinseiForm.riyoBasho.trim() === '') {
              callback(CheckMsg.CHK_SHINSEI_RIYO_BASHO)
            }
          }
        }
      ],
      riyomokuteki: [
        {
          required: true,
          trigger: "blur",
          message: CheckMsg.CHK_SHINSEI_RIYO_MOKUTEKI,
        },
        {
          validator: async (self: any, value: string, callback: (message: string) => void) => {
            if (panels.shinseiForm.riyomokuteki.trim() === '') {
              callback(CheckMsg.CHK_SHINSEI_RIYO_MOKUTEKI)
            }
          }
        }
      ],
      riyohoho: [
        {
          required: true,
          trigger: "blur",
          message: CheckMsg.CHK_SHINSEI_RIYO_HOHO,
        },
        {
          validator: async (self: any, value: string, callback: (message: string) => void) => {
            if (panels.shinseiForm.riyohoho.trim() === '') {
              callback(CheckMsg.CHK_SHINSEI_RIYO_HOHO)
            }
          }
        }
      ],
      riyohani: [
        {
          validator: async (self: any, value: string, callback: any) => {
            const {
              riyohaniDcfFlg,
              riyohaniDsfFlg,
              riyohaniDnfFlg,
              riyohaniEcfFlg,
              riyohaniPcfFlg,
              riyohaniSonohokaFlg,
              riyohaniSonohokaText,
            } = panels.shinseiForm;
            if (
              !(
                riyohaniDcfFlg ||
                riyohaniDsfFlg ||
                riyohaniDnfFlg ||
                riyohaniEcfFlg ||
                riyohaniPcfFlg ||
                riyohaniSonohokaFlg
              )
            ) {
              callback(CheckMsg.CHK_SHINSEI_RIYO_HANI);
            } else if (riyohaniSonohokaFlg && !riyohaniSonohokaText) {
              callback(CheckMsg.CHK_SHINSEI_RIYO_SONOHOKA_TEXT);
            }
          },
          trigger: "change",
        },
      ],
      riyokomoku: [
        {
          validator: async (self: any, value: string, callback: any) => {
            const {
              riyokomokuKjnCdFlg,
              riyokomokuKjnNmFlg,
              riyokomokuShiCdFlg,
              riyokomokuShiNmFlg,
              riyokomokuShiJushoFlg,
              riyokomokuShiTelFlg,
              riyokomokuSrkmFlg,
              riyokomokuSzbkFlg,
              riyokomokuSonohokaFlg,
              riyokomokuSonohokaText,
            } = panels.shinseiForm;
            if (
              !(
                riyokomokuKjnCdFlg ||
                riyokomokuKjnNmFlg ||
                riyokomokuShiCdFlg ||
                riyokomokuShiNmFlg ||
                riyokomokuShiJushoFlg ||
                riyokomokuShiTelFlg ||
                riyokomokuSrkmFlg ||
                riyokomokuSzbkFlg ||
                riyokomokuSonohokaFlg
              )
            ) {
              callback(CheckMsg.CHK_SHINSEI_RIYO_KOMOKU);
            } else if (riyokomokuSonohokaFlg && !riyokomokuSonohokaText) {
              callback(CheckMsg.CHK_SHINSEI_RIYO_SONOHOKA_TEXT);
            }
          },
          trigger: "change",
        },
      ],
      doiCheck: [
        {
          validator: async (self: any, value: string, callback: any) => {
            if (!panels.shinseiForm.doiCheck) {
              callback(CheckMsg.CHK_SHINSEI_DOI);
            }
          },
          trigger: "change",
        },
      ],
    };
    const shoninFormRules = {
      shinseiBunruiCd: [
        {
          required: true,
          message: CheckMsg.CHK_SHINSEI_BUNRUI_NM,
          trigger: "blur",
        },
      ],
      shoninComment: [
        {
          required: true,
          message: CheckMsg.CHK_ULT_COMMENT,
          trigger: "blur",
        },
      ],
    };

    // 画面情報セット
    // 申請のシーケンスidを取得
    const id = route.params.id;
    // 記入欄活性制御
    const panelControl = async () => {
      const isShinseisha = userStore.isShinseisha;
      if (isShinseisha) {
        //ユーザーが申請者、かつステータスが申請前、保留の場合のみ申請内容記入可能
        if (
          panels.shinseiInfo.statusCd === Status.期限切 ||
          panels.shinseiInfo.statusCd === Status.申請中 ||
          panels.shinseiInfo.statusCd === Status.承認
        ) {
          isShinseiPanelDisable.value = true;
        }
        //ユーザーが申請者、かつステータスが保留、承認の場合のみ承認内容欄表示
        if (
          panels.shinseiInfo.statusCd === Status.保留 ||
          panels.shinseiInfo.statusCd === Status.承認
        ) {
          isShoninPanelVisible.value = true;
        }
      } else {
        //承認者の場合、申請内容記入不可、承認内容欄表示
        isShinseiPanelDisable.value = true;
        isShoninPanelVisible.value = true;
        //申請中の場合のみ、承認内容記入可
        if (panels.shinseiInfo.statusCd === Status.申請中) {
          isShoninPanelDisable.value = false;
        }
        //承認内容欄の申請分類リストload
        const {
          data: { result },
        } = await getShinseiBunruiList();
        shinseiBunruiOptions.value = result.listData;
      }
    };
    //buttons表示制御
    const buttonsControl = async () => {
      //ログインユーザーは申請登録者か判断フラグ
      const isTorokusha =
        userStore.getAccountId === panels.shinseiForm.torokuAccountId;

      //ログインユーザーの会社グループと申請登録者のは同じか判断フラグ
      let isSameGroup = false;
      if (isTorokusha) {
        isSameGroup = true;
      } else if (id && userStore.isShinseisha) {
        const { data: data2 } = await getIsSameGroupFlg(
          panels.shinseishaInfo.groupId
        );
        isSameGroup = data2.result;
      }

      console.log(
        userStore.getRole,
        panels.shinseiInfo.statusCd,
        panels.shinseiInfo.shinchokuCdShinsei,
        panels.shinseiInfo.shinchokuCdShonin,
        isTorokusha,
        isSameGroup
      );
      Array.from(displayPatterns).forEach(([key, value]) => {
        if (
          key.status === panels.shinseiInfo.statusCd &&
          key.shinchoku ===
            (panels.shinseiInfo.shinchokuCdShinsei ||
              panels.shinseiInfo.shinchokuCdShonin ||
              "") &&
          key.role === userStore.getRole &&
          (key.isTorokusha === undefined
            ? true
            : key.isTorokusha === isTorokusha) &&
          (key.isSameGroup === undefined
            ? true
            : key.isSameGroup === isSameGroup)
        ) {
          console.log(key);
          value();
        }
      });
      if (Object.values(displayButtons).every((item) => !item)) {
        isShinseiPanelDisable.value = true;
        isShoninPanelDisable.value = true;
      }
    };
    // 情報セット
    const setDetailInfo = async () => {
      try {
        if (id) {
          const {
            data: { result },
          } = await getShinseiById(id as string);
          panels.shinseiInfo.id = result.id;
          panels.shinseiInfo.shinseiId = result.shinsei_id;
          panels.shinseiInfo.statusCd = result.status.status_cd;
          panels.shinseiInfo.status = result.status.status;
          panels.shinseiInfo.shinchokuCdShinsei =
            result.kigyonai_shinchoku_shinsei?.kigyonai_shinchoku_cd;
          panels.shinseiInfo.shinchokuShinsei =
            result.kigyonai_shinchoku_shinsei?.kigyonai_shinchoku;
          panels.shinseiInfo.shinchokuCdShonin =
            result.kigyonai_shinchoku_shonin?.kigyonai_shinchoku_cd;
          panels.shinseiInfo.shinchokuShonin =
            result.kigyonai_shinchoku_shonin?.kigyonai_shinchoku;
          panels.shinseiInfo.torokuDate = dateFormat(result.toroku_date);
          panels.shinseiInfo.kousinDate = dateFormat(result.koshin_date);

          panels.shinseishaInfo.kaishaNm =
            result.toroku_shinseisha.kaishaGroup.kaisha.kaisha_nm;
          panels.shinseishaInfo.groupId =
            result.toroku_shinseisha.kaishaGroup.id;
          panels.shinseishaInfo.groupNm =
            result.toroku_shinseisha.kaishaGroup.kaisha_group_nm;
          panels.shinseishaInfo.torokuKjnNm = result.toroku_shinseisha.kjn_nm;
          panels.shinseishaInfo.torokuBushoNm =
            result.toroku_shinseisha.busho_nm;
          panels.shinseishaInfo.kanriKjnNm = result.kanri_shinseisha?.kjn_nm;
          panels.shinseishaInfo.kanriBushoNm =
            result.kanri_shinseisha?.busho_nm;

          panels.shoninshaInfo.torokuTantoNm =
            result.toroku_shoninsha?.tanto_nm;
          panels.shoninshaInfo.kanriTantoNm = result.kanri_shoninsha?.tanto_nm;

          panels.shinseiForm.itakusakiKaishaNm = result.ss_itakusaki_kaisha_nm;
          panels.shinseiForm.itakusakiJusho = result.ss_itakusaki_jusho;
          panels.shinseiForm.saiItakusakiKaishaNm =
            result.ss_saiitakusaki_kaisha_nm;
          panels.shinseiForm.saiItakusakiJusho = result.ss_saiitakusaki_jusho;
          panels.shinseiForm.kikanKaishibi = result.ss_kikan_kaishibi
            ? dayjs(result.ss_kikan_kaishibi).toDate()
            : null;
          panels.shinseiForm.kikanShuryobi = result.ss_kikan_shuryobi
            ? dayjs(result.ss_kikan_shuryobi).toDate()
            : null;
          panels.shinseiForm.riyoBasho = result.ss_riyo_basho;
          panels.shinseiForm.riyomokuteki = result.ss_riyomokuteki;
          panels.shinseiForm.riyohoho = result.ss_riyohoho;
          panels.shinseiForm.riyohaniDcfFlg = result.ss_riyohani_dcf_flg;
          panels.shinseiForm.riyohaniDsfFlg = result.ss_riyohani_dsf_flg;
          panels.shinseiForm.riyohaniDnfFlg = result.ss_riyohani_dnf_flg;
          panels.shinseiForm.riyohaniEcfFlg = result.ss_riyohani_ecf_flg;
          panels.shinseiForm.riyohaniPcfFlg = result.ss_riyohani_pcf_flg;
          panels.shinseiForm.riyohaniSonohokaFlg =
            result.ss_riyohani_sonohoka_flg;
          panels.shinseiForm.riyohaniSonohokaText =
            result.ss_riyohani_sonohoka_text;
          panels.shinseiForm.riyokomokuKjnCdFlg =
            result.ss_riyokomoku_kjncd_flg;
          panels.shinseiForm.riyokomokuKjnNmFlg =
            result.ss_riyokomoku_kjnnm_flg;
          panels.shinseiForm.riyokomokuShiCdFlg =
            result.ss_riyokomoku_shicd_flg;
          panels.shinseiForm.riyokomokuShiNmFlg =
            result.ss_riyokomoku_shinm_flg;
          panels.shinseiForm.riyokomokuShiJushoFlg =
            result.ss_riyokomoku_shijusho_flg;
          panels.shinseiForm.riyokomokuShiTelFlg =
            result.ss_riyokomoku_shitel_flg;
          panels.shinseiForm.riyokomokuSrkmFlg = result.ss_riyokomoku_srkm_flg;
          panels.shinseiForm.riyokomokuSzbkFlg = result.ss_riyokomoku_szbk_flg;
          panels.shinseiForm.riyokomokuSonohokaFlg =
            result.ss_riyokomoku_sonohoka_flg;
          panels.shinseiForm.riyokomokuSonohokaText =
            result.ss_riyokomoku_sonohoka_text;
          panels.shinseiForm.shinseiComment = result.shinsei_comment;
          panels.shinseiForm.doiCheck = result.doi_check;
          panels.shinseiForm.torokuAccountId =
            result.toroku_shinseisha.account.akaunto_id;

          panels.shoninForm.shinseiBunruiCd =
            result.shinsei_bunrui?.shinsei_bunrui_cd;
          panels.shoninForm.shoninComment = result.shonin_comment;
          panels.shoninForm.shoninTorokuAccountId =
            result.toroku_shoninsha?.account.akaunto_id;
          panels.shoninForm.shoninKanriAccountId =
            result.kanri_shoninsha?.account.akaunto_id;
        }

        await panelControl();
        await buttonsControl();
      } catch (error) {
        router.push({ name: "shinseiList" });
      }
    };
    setDetailInfo();

    // 各種ボタン押下イベント
    // 承認者用ボタン組
    const {
      handleShonin,
      handleKariShonin,
      handleHoryu,
      handleKariHoryu,
      handleShoninOk,
      handleHoryuOk,
      handleShoninNg,
      handleHoryuNg,
    } = handleShoninshaButtons(shoninFormRef, panels, handleReload, loadings);
    //申請者用ボタン組
    const {
      handleShinsei,
      handleKariShinsei,
      handleShinseiOk,
      handleShinseiNg,
      handleSaveShinsei,
      handleCopyShinsei,
      handleDeleteShinsei,
    } = handleShinseishaButtons(
      shinseiFormRef,
      panels,
      handleReload,
      loadings,
      Object.keys(shinseiFormRules)
    );
    //キャンセルボタン押下
    const handleCancel = () => {
      router.push({ name: "shinseiList" });
    };

    // 申請分類選択ダイアログ用
    const { selectDialogVisible, handleSelect, handleCloseDialog } =
      useSelectDialog();
    const handleSelectInfo = (row: shoninCommentHojoInfo) => {
      panels.shoninForm.shoninComment =
        row.shoninComment +
        (panels.shoninForm.shoninComment
          ? "\n\n────────────────────────────────────────────────────────────────────────────────────────────────\n\n" +
            panels.shoninForm.shoninComment
          : "");
      handleCloseDialog();
    };

    return {
      handleSelectInfo,
      selectDialogVisible,
      handleSelect,
      handleCloseDialog,
      panels,
      shinseiFormRef,
      shoninFormRef,
      shinseiFormRules,
      shoninFormRules,
      loadings,
      shinseiBunruiOptions,
      ELEMENT_ACCESS,
      isShinseiPanelDisable,
      id,
      statusFilter,
      handleCancel,
      isShoninPanelDisable,
      isShoninPanelVisible,
      displayButtons,
      handleShonin,
      handleKariShonin,
      handleHoryu,
      handleKariHoryu,
      handleShoninOk,
      handleHoryuOk,
      handleShoninNg,
      handleHoryuNg,
      handleShinsei,
      handleKariShinsei,
      handleShinseiOk,
      handleShinseiNg,
      handleSaveShinsei,
      handleCopyShinsei,
      handleDeleteShinsei,
    };
  },
});
